import { importWrappersModuleFederation } from 'app/constants/mfLazyImports'
import { ETranslationScope, translationAPI } from 'legacy/hpscoutso/classes/api/TranslationAPI'
import InitializationServiceHpScout from 'legacy/hpscoutso/classes/services/InitializationServiceHpScout'
import { PartnerIdTokenHelper } from 'legacy/lut/classes/helpers/partner360/getPartnerIdFromToken'
import InitializationServiceLut from 'legacy/lut/classes/services/InitializationService'
import InitializationServiceScout from 'legacy/lut/classes/services/InitializationServiceScout'
import InitializationServiceOnboarding from 'legacy/lut/classes/services/onboarding/InitializationServiceOnboarding'
import InitializationServicePartner360 from 'legacy/lut/classes/services/partner360/InitializationServicePartner360'
import userEventService from 'legacy/lut/classes/userEvents/UserEventService'
import { ReferenceWrapper } from 'legacy/lut/hoc/ReferenceWrapper'
import { useCallback } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useDispatch, useSelector } from 'react-redux'

import { AmplifyImpactKeyInitiativeEditor } from 'clickNgo/features/amplifyImpactKeyInitiativeEditor'
import { initTranslationService } from 'cng-common/features/translations'
import { useImportDependency } from 'core-components/hooks/useImportDependency'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { wsConnectionPool } from 'core/classes/ws/WSConnectionPool'
import { AppLoader } from 'core/components/app/AppLoader'
import { CoreLoaderWrapper, ILoader } from 'core/components/app/loaders'
import Logger from 'core/utils/Logger'
import InitializationServiceDialer from 'dialer/classes/services/InitializationServiceDialer'
import { DEFAULT_LANGUAGE, LanguageProvider } from 'onboarding/context/LanguageContext'
import { usePlanningDefaultViewRedirect } from 'partner360/hooks/usePlanningDefaultViewRedirect'
import settings from 'src/config/settings'

const TRANSLATIONS_LOADERS = [translationAPI.fetchTranslations]

export const AppLoaderWrapper = ({ children, ...props }: ILoader) => {
  const user = useSelector(selectUser)
  const ownerId = useSelector(selectAppOwnerId)
  const dispatch = useDispatch<any>()
  const isAuthorized = Boolean(user.id)

  usePlanningDefaultViewRedirect()

  const { isDependencyLoading: isAmplifyImpactInjectorLoading, Module: AmplifyImpactInjector } =
    useImportDependency({
      key: importWrappersModuleFederation.amplify.key,
      importFunction: importWrappersModuleFederation.amplify.importFn,
    })

  const { isDependencyLoading: isKbaseWrapperLoading, Module: KbaseModuleWrapper } =
    useImportDependency({
      key: importWrappersModuleFederation.kbase.key,
      importFunction: importWrappersModuleFederation.kbase.importFn,
      defaultValue: ({ children }) => children,
    })

  const { isDependencyLoading: isDocflowWrapperLoading, Module: DocflowModuleWrapper } =
    useImportDependency({
      key: importWrappersModuleFederation.docflow.key,
      importFunction: importWrappersModuleFederation.docflow.importFn,
      defaultValue: ({ children }) => children,
    })

  const isModuleFederationsLoading =
    isAmplifyImpactInjectorLoading || isDocflowWrapperLoading || isKbaseWrapperLoading

  const beforeInitCallback = useCallback(async () => {
    wsConnectionPool.initWSPool(settings.wsServices)
    if (isAuthorized) {
      userEventService.register('login').catch(Logger.error)
    }

    const mainServicesInitializationArray: Promise<unknown>[] = [
      InitializationServiceOnboarding.init(),
      PartnerIdTokenHelper.initializePartnerId(ownerId),
      InitializationServiceDialer.init(),
    ]

    await Promise.all(mainServicesInitializationArray)

    InitializationServiceLut.init()
    InitializationServiceScout.init()
    InitializationServicePartner360.init()
    InitializationServiceHpScout.init()

    if (isAuthorized) {
      await Promise.resolve(dispatch(initTranslationService()))
    }
  }, [dispatch, ownerId, isAuthorized])

  if (isModuleFederationsLoading) {
    return <AppLoader />
  }

  return (
    <CoreLoaderWrapper {...props} beforeInitCallback={beforeInitCallback}>
      <ReferenceWrapper>
        <KbaseModuleWrapper>
          <DocflowModuleWrapper>
            <AmplifyImpactInjector />
            <DndProvider backend={HTML5Backend}>
              <LanguageProvider
                translationsLoaders={TRANSLATIONS_LOADERS}
                defaultScope={ETranslationScope.partner360Hpscoutso}
                fallbackLanguage={DEFAULT_LANGUAGE}
              >
                <AmplifyImpactKeyInitiativeEditor />
                {children}
              </LanguageProvider>
            </DndProvider>
          </DocflowModuleWrapper>
        </KbaseModuleWrapper>
      </ReferenceWrapper>
    </CoreLoaderWrapper>
  )
}
